import React, { useId, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    buildLotValueView, formatMoneyShort, formatMoneySigned, lotCoverageText, LOT_VALUE_TIPS,
} from '../../utils/lotValue';
import { useDealershipSettings } from '../../contexts/settingsContext';
import './LotValueTile.css';

const TIPS = LOT_VALUE_TIPS;

// Focusable figure with an aria-describedby tooltip: shows on hover, keyboard
// focus and tap (a tap focuses the tabIndex element), not only via `title`.
function Figure({ id, testId, tip, className = '', children }) {
    const tipId = `${id}-${testId}-tip`;
    return (
        <div className={`lot-figure ${className}`.trim()} data-testid={testId} tabIndex={0} aria-describedby={tipId}>
            {children}
            <span id={tipId} role="tooltip" className="lot-tip">
                {tip}
            </span>
        </div>
    );
}

Figure.propTypes = {
    id: PropTypes.string.isRequired,
    testId: PropTypes.string.isRequired,
    tip: PropTypes.string.isRequired,
    className: PropTypes.string,
    children: PropTypes.node,
};

// Small (i) next to the title with the same focusable tooltip pattern as the
// figures: hover, keyboard focus and tap all reveal it.
function CoverageInfo({ id, text }) {
    const tipId = `${id}-lot-coverage-tip`;
    return (
        <span
            className="lot-info"
            data-testid="lot-coverage-info"
            tabIndex={0}
            role="img"
            aria-label="Coverage"
            aria-describedby={tipId}
        >
            <svg className="lot-info-icon" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
                <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" />
                <rect x="7.25" y="7" width="1.5" height="4.5" rx="0.75" fill="currentColor" />
                <circle cx="8" cy="4.75" r="0.9" fill="currentColor" />
            </svg>
            <span id={tipId} role="tooltip" className="lot-tip">
                {text}
            </span>
        </span>
    );
}

CoverageInfo.propTypes = {
    id: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
};

function LotValueTile({ units = [] }) {
    const baseId = useId().replace(/:/g, '');
    const { gapThresholds } = useDealershipSettings();
    const view = useMemo(() => buildLotValueView(units, gapThresholds), [units, gapThresholds]);
    const { emptyText } = view;

    return (
        <div className="summary-panel lot-value-panel">
            <div className="panel-header">
                <h3>Used lot value</h3>
                {!emptyText && <CoverageInfo id={baseId} text={lotCoverageText(view)} />}
            </div>

            {emptyText ? (
                <p className="lot-empty">{emptyText}</p>
            ) : (
                <>
                    <div className="lot-figures">
                        <Figure id={baseId} testId="lot-asking" tip={TIPS.asking}>
                            <span className="lot-figure-label">Asking</span>
                            <span className="lot-figure-value">{formatMoneyShort(view.askingTotal)}</span>
                        </Figure>
                        <Figure id={baseId} testId="lot-ai" tip={TIPS.ai}>
                            <span className="lot-figure-label">AI value</span>
                            <span className="lot-figure-value">{formatMoneyShort(view.aiTotal)}</span>
                        </Figure>
                        <Figure
                            id={baseId}
                            testId="lot-gap"
                            tip={TIPS.gap}
                            className={`lot-gap lot-gap--${view.severity}`}
                        >
                            <span className="lot-figure-label">Gap</span>
                            <span className="lot-figure-value">{formatMoneySigned(view.gapTotal)}</span>
                            <span className="lot-figure-pct">{view.pctText}</span>
                            {view.hint && (
                                <span className="lot-figure-hint" data-testid="lot-gap-hint">
                                    {view.hint}
                                </span>
                            )}
                        </Figure>
                    </div>
                </>
            )}
        </div>
    );
}

LotValueTile.propTypes = {
    units: PropTypes.arrayOf(PropTypes.object),
};

export default LotValueTile;
